import React, { useCallback } from 'react';
import { arrayOf, func, shape } from 'prop-types';

// utils
import { useUserRolePermissions } from 'hooks';
import { AUTH_SEQUENCING_ADD_AUTH } from 'feature/loading-management/utils/const';

// Components
import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import EmptyState from './EmptyState';
import DragAndDropButtonGroup from './DragAndDropButtonGroup';
import { detailLabelStyles, withLeftPaddingStyles } from '../EditPanel.styles';

const Sequenced = props => {
  const { sequencedAuthorizations, badgeProps, onMove, onRemoveSeq } = props;
  const { isProductLoadingManage } = useUserRolePermissions();
  const theme = useTheme();

  const onDragEnd = useCallback(
    sequence => {
      onMove(sequence.source.index, sequence.destination.index);
    },
    [onMove]
  );

  return (
    <Box color="primary.dark" pt={3} px={3}>
      <Typography color="inherit" sx={detailLabelStyles(theme)} variant="subtitle1">
        Current Sequence
      </Typography>
      {!sequencedAuthorizations.length ? (
        <EmptyState
          icon={<AddCircleOutlineRoundedIcon sx={{ fontSize: 32 }} />}
          text={AUTH_SEQUENCING_ADD_AUTH}
        />
      ) : (
        <>
          <Divider />

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {provided => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {sequencedAuthorizations.map(
                    (
                      { active, inventoryOwner, productAuthorizationId, supplierDirectedBlend },
                      idx
                    ) => {
                      const currentBadgeProps = badgeProps(
                        active,
                        inventoryOwner,
                        supplierDirectedBlend
                      );

                      return (
                        <Draggable
                          draggableId={`${productAuthorizationId}${
                            supplierDirectedBlend ? '-SDB' : ''
                          }`}
                          index={idx}
                          isDragDisabled={!isProductLoadingManage}
                          key={`${productAuthorizationId}${supplierDirectedBlend ? '-SDB' : ''}`}
                        >
                          {_provided => (
                            <div
                              ref={_provided.innerRef}
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {..._provided.draggableProps}
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {..._provided.dragHandleProps}
                            >
                              <Box
                                alignItems="center"
                                display="flex"
                                justifyContent="space-between"
                                py={1.5}
                                width={1}
                              >
                                <Box
                                  sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Tooltip
                                    enterTouchDelay={10}
                                    {...currentBadgeProps?.tooltipProps}
                                    title={currentBadgeProps?.title}
                                  >
                                    <Chip
                                      label={idx + 1}
                                      type={currentBadgeProps?.type}
                                      variant="badge"
                                    />
                                  </Tooltip>
                                  <Typography
                                    color="textPrimary"
                                    sx={withLeftPaddingStyles(theme)}
                                    variant="body2"
                                  >
                                    {supplierDirectedBlend
                                      ? 'Supplier Directed Blend'
                                      : inventoryOwner.nameShort}
                                  </Typography>
                                </Box>
                                {isProductLoadingManage && (
                                  <DragAndDropButtonGroup idx={idx} onRemoveSeq={onRemoveSeq} />
                                )}
                              </Box>
                            </div>
                          )}
                        </Draggable>
                      );
                    }
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
    </Box>
  );
};

export default Sequenced;

Sequenced.propTypes = {
  badgeProps: func.isRequired,
  onMove: func.isRequired,
  onRemoveSeq: func.isRequired,
  sequencedAuthorizations: arrayOf(shape({})),
};

Sequenced.defaultProps = {
  sequencedAuthorizations: [],
};
